/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { css, jsx } from '@emotion/react'
import React, { Fragment, type FunctionComponent } from 'react'

import {
  EuiButton,
  EuiButtonEmpty,
  EuiFlexGroup,
  EuiForm,
  EuiHorizontalRule,
  EuiSpacer,
  EuiText,
  EuiTitle,
  useIsWithinBreakpoints,
} from '@elastic/eui'

import { DocLink } from '@modules/doclinks'
import { FormattedMessage } from '@modules/i18n'
import { CuiAlert } from '@modules/cui/Alert'

import notificationMessages from '../../../messages'
import useBillingNotificationsNav from '../../../hooks/useBillingNotificationsNav'

import Configuration from './Configuration'
import Alerts from './Alerts'
import useBudgetSettings from './useBudgetSettings'

import type { Budget, BudgetConfigurationValues } from '../../types'

const MAX_WIDTH_IN_LARGER_SCREENS = '65%'

const submitButton = {
  create: {
    id: 'billing.notifications.budgets.budget-settings.button.create',
    defaultMessage: 'Create budget',
  },
  update: {
    id: 'billing.notifications.budgets.budget-settings.button.save',
    defaultMessage: 'Save budget',
  },
}

export type Props = {
  alerts: Array<Pick<Budget['alerts'][number], 'threshold' | 'threshold_type'>>
  initialValues?: BudgetConfigurationValues
  onSubmit: (values: BudgetConfigurationValues) => void
  isMutationError: boolean
  isLoading?: boolean
  isSubmitting: boolean
  title: React.ReactNode
}

const BudgetSettings: FunctionComponent<Props> = ({
  alerts,
  initialValues,
  onSubmit,
  isMutationError,
  isLoading = false,
  isSubmitting,
  title,
}) => {
  const { goToBillingNotifications } = useBillingNotificationsNav()
  const isLargeScreen = useIsWithinBreakpoints(['l', 'xl'])

  const budgetSettings = useBudgetSettings({ initialValues, onSubmit })

  const isEditingMode = !!initialValues

  const handleCancelBudget = () => goToBillingNotifications()

  return (
    <EuiForm component='form' onSubmit={budgetSettings.handleSubmit} noValidate={true}>
      <EuiFlexGroup
        direction='column'
        gutterSize='none'
        css={css(isLargeScreen && { maxWidth: MAX_WIDTH_IN_LARGER_SCREENS })}
      >
        <EuiTitle size='l'>
          <h2>{title}</h2>
        </EuiTitle>
        <EuiSpacer size='m' />
        <EuiText size='s' color='subdued'>
          <FormattedMessage
            id='billing.notifications.budgets.budget-settings.description'
            defaultMessage='Set a spending threshold for your organization to track usage and avoid surprises on your bill. {learnMore}'
            values={{
              learnMore: (
                <DocLink
                  data-test-subj='billing.notifications.budgets.budget-settings.description.learn-more'
                  link='billingCreateBudgetEmails'
                  showExternalLinkIcon={true}
                >
                  <FormattedMessage
                    id='billing.notifications.budgets.budget-settings.description.learn-more'
                    defaultMessage='Learn more'
                  />
                </DocLink>
              ),
            }}
          />
        </EuiText>

        <EuiHorizontalRule margin='l' />
        <Configuration
          errors={budgetSettings.errors}
          handleChange={budgetSettings.handleChange}
          isProcessing={isLoading || isSubmitting}
          values={budgetSettings.values}
        />
        <EuiHorizontalRule margin='xl' />
        <Alerts alerts={alerts} />
      </EuiFlexGroup>

      {isMutationError && (
        <Fragment>
          <EuiSpacer size='m' />

          <CuiAlert iconType='alert' type='error'>
            <FormattedMessage {...notificationMessages.genericErrorMessage} />
          </CuiAlert>
        </Fragment>
      )}

      <EuiSpacer size='xxl' />
      <EuiFlexGroup justifyContent='flexEnd' gutterSize='s'>
        <EuiButtonEmpty
          aria-label='Cancel'
          data-test-subj='billing.notifications.budgets.budget-settings.button.cancel'
          disabled={isSubmitting}
          onClick={handleCancelBudget}
          size='m'
        >
          <FormattedMessage
            id='billing.notifications.budgets.budget-settings.button.cancel'
            defaultMessage='Cancel'
          />
        </EuiButtonEmpty>
        <EuiButton
          color='primary'
          data-test-subj='billing.notifications.budgets.budget-settings.button.save'
          disabled={isLoading || isSubmitting}
          isLoading={isSubmitting}
          fill={true}
          type='submit'
          size='m'
        >
          <FormattedMessage {...submitButton[isEditingMode ? 'update' : 'create']} />
        </EuiButton>
      </EuiFlexGroup>
    </EuiForm>
  )
}

export default BudgetSettings
